import React from 'react'
import { IconCheckDefault, IconCloseDefault } from '@mc/design-system'
import { useTranslation, withI18nContext } from '@mc/i18n'
import {
  BenefitTrans,
  useBenefitTranslation,
  type PlansTier,
} from '@mc/plans-ui'
import { PricingCell } from '../../../../../components/Matrix/features/PricingFeature/components/DefaultPricingFeature/PricingCell'
import { CHECKOUT_I18N_NAMESPACE } from '../../../../../i18n'
import { trackCertificatesRowViewed } from '../../../events/rowViewed'
import { useMCProCertsCatalogValue } from '../../../hooks/useMCProCertsCatalogValue'
import { MC_PRO_TIER_I18N_NAMESPACE, i18nInstance } from '../../../i18n'
import { CertificatesTitle, CertificatesValue } from '../../CertificatesFeature'
import { Row } from './Table'

export const PricingFeature = () => {
  const { t } = useTranslation(CHECKOUT_I18N_NAMESPACE)

  return (
    <Row title={t('plans.features.pricing.title')}>
      {({ tier }) => <PricingCell tier={tier} />}
    </Row>
  )
}

export const DevicesFeature = () => {
  const { t } = useTranslation(CHECKOUT_I18N_NAMESPACE)
  const { t: tMCPro } = useTranslation(MC_PRO_TIER_I18N_NAMESPACE)

  return (
    <Row
      title={t('plans.features.devices.title')}
      subtitle={tMCPro('matrix.devices.subtitle')}
    >
      {({ tier }) => (
        <BenefitTrans
          tier={tier}
          name='concurrent_devices'
          i18nKey='plans.features.devices.counter'
          ns={CHECKOUT_I18N_NAMESPACE}
        />
      )}
    </Row>
  )
}

export const OfflineDownloadIcon = ({ tier }: { tier: PlansTier }) => {
  const { t: tb } = useBenefitTranslation({
    name: 'offline_download',
    ns: CHECKOUT_I18N_NAMESPACE,
  })

  const { value: isIncluded } =
    tier.entitlement?.benefits?.find(
      (benefit) => benefit.name === 'offline_download',
    ) || {}

  const Icon = isIncluded ? IconCheckDefault : IconCloseDefault

  return (
    <Icon
      size={2}
      srText={tb('plans.features.offlineDownload.included', {
        tier,
        defaultValue: false,
      })}
    />
  )
}

export const OfflineDownloadFeature = () => {
  const { t } = useTranslation(CHECKOUT_I18N_NAMESPACE)

  return (
    <Row title={t('plans.features.offlineDownload.title')}>
      {({ tier }) => <OfflineDownloadIcon tier={tier} />}
    </Row>
  )
}

export const CertificatesFeature = withI18nContext(i18nInstance)(() => {
  const { t } = useTranslation(MC_PRO_TIER_I18N_NAMESPACE)
  const value = useMCProCertsCatalogValue()

  return (
    <Row
      title={
        <CertificatesTitle title={t('matrix.certificates.annualPass.title')} />
      }
      subtitle={
        value
          ? t('matrix.certificates.annualPass.description', { value })
          : t('matrix.certificates.annualPass.descriptionWithoutValue')
      }
      onFirstVisible={() => trackCertificatesRowViewed(4)}
    >
      {({ isPro }) => (
        <CertificatesValue value={isPro ? 'included' : 'notIncluded'} />
      )}
    </Row>
  )
})
